import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import { Spinner } from "@performyard/py-components";

import { AskPyEmptyState } from "./AskPyEmptyState";
import { AskPyPanelHeader } from "./AskPyPanelHeader";

import { useAskStream } from "../../hooks/useAskStream";
import { useFormAuthoringCoachEnabled } from "../../hooks/useFormAuthoringCoachEnabled";
import { useFormQuickActionEnabled } from "../../hooks/useFormQuickActionEnabled";
import { useIsAuthoringReviewForm } from "../../hooks/useIsAuthoringReviewForm";
import { useRestoreActiveConversation } from "../../hooks/useRestoreActiveConversation";

import { useAskPerformYardStore } from "../../AskPerformYardStore";
import { FORM_AUTHORING_COACH_START_QUERY } from "../../constants";
import { ChatErrorBanner } from "../ChatErrorBanner";
import ChatInput from "../ChatInput";
import ChatMessages from "../ChatMessages";
import { FormAuthoringCoachSuggestedActions } from "../FormAuthoringCoachSuggestedActions";
import { FormBuilderSuggestedActions } from "../FormBuilderSuggestedActions";
import { GoalCoachSuggestedActions } from "../GoalCoachSuggestedActions";

/** Full-height docked Ask PY panel (PY-13558 shell). Non-modal: no backdrop, no scroll lock — the page stays usable beside it. */
export const AskPyPanel: React.FC = () => {
	const isPanelOpen = useAskPerformYardStore((state) => state.isPanelOpen);
	const messages = useAskPerformYardStore((state) => state.messages);
	const setError = useAskPerformYardStore((state) => state.setError);
	const closePanel = useAskPerformYardStore((state) => state.closePanel);
	const conversationStarted = useAskPerformYardStore((state) => state.messages.some((m) => m.role === "user"));

	const askHrEnabled = useAskPerformYardStore((state) => state.cachedAskHrEnabled);
	const goalCreationEnabled = useAskPerformYardStore((state) => state.cachedGoalCreationEnabled);
	const formCreationEnabled = useAskPerformYardStore((state) => state.cachedFormCreationEnabled);
	const goalCoachingEnabled = useAskPerformYardStore((state) => state.cachedGoalCoachingEnabled);
	const formBuildingCoachEnabled = useAskPerformYardStore((state) => state.cachedFormBuildingCoachEnabled);
	const hasNoDocuments = useAskPerformYardStore((state) => state.hasNoDocuments);
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	const pendingFormAuthoringCoachStart = useAskPerformYardStore((state) => state.pendingFormAuthoringCoachStart);
	const consumePendingFormAuthoringCoachStart = useAskPerformYardStore(
		(state) => state.consumePendingFormAuthoringCoachStart
	);

	// Review form authoring only — a survey taker is not filling out a review, so the coach has
	// nothing to evaluate and its action stays off that page.
	const formAuthoringCoachEnabled = useFormAuthoringCoachEnabled();
	const isAuthoringReviewForm = useIsAuthoringReviewForm();

	const isLoadingConversation = useAskPerformYardStore((state) => state.isLoadingConversation);

	// Owned here (not inside ChatMessages/ChatInput) so unmount cleanup can't abort
	// an in-flight fetch — mirrors ChatContainer.tsx's rationale for the FAB's ChatContent.
	const { sendQuery } = useAskStream();

	// Re-opens on the conversation the user was last in after a page reload.
	useRestoreActiveConversation();

	// Live hook rather than the cached flag: the suggest-questions chip must follow the user's
	// form-creation permission, not just the company setting (same read the empty-state card used).
	const formQuickActionEnabled = useFormQuickActionEnabled();

	// Return focus to the nav trigger when the panel closes (WCAG 2.4.3). The trigger renders in the
	// top bar's own portal tree, so it's reached by testid rather than a ref threaded across trees.
	useEffect(() => {
		if (!isPanelOpen) return;
		return () => {
			document.querySelector<HTMLElement>('[data-testid="ask-py-nav-trigger"]')?.focus();
		};
	}, [isPanelOpen]);

	// The Form Authoring Coach callout parks this flag when its CTA opens the panel; the send happens
	// here so it isn't aborted by the callout's own unmount. Deferred a tick so the fetch lands on the
	// stable mount — mirrors ChatContainer.tsx's goal-coach hand-off.
	useEffect(() => {
		if (!pendingFormAuthoringCoachStart) return;
		const timer = setTimeout(() => {
			if (consumePendingFormAuthoringCoachStart()) {
				void sendQuery({ query: FORM_AUTHORING_COACH_START_QUERY, agent: "form-authoring-coach" });
			}
		}, 0);
		return () => clearTimeout(timer);
	}, [pendingFormAuthoringCoachStart, consumePendingFormAuthoringCoachStart, sendQuery]);

	if (!isPanelOpen) return null;

	// Ask HR is the only capability and there's nothing for it to read, so the thread is replaced by an
	// explanation instead of a dead input over an empty card grid. Deliberately the cached flags rather
	// than the live hooks: this has to be the same predicate ChatInput disables itself on.
	const showNoDocumentsError =
		askHrEnabled &&
		hasNoDocuments &&
		!goalCreationEnabled &&
		!formCreationEnabled &&
		!(isOnGoalCreationPage && goalCoachingEnabled) &&
		!(isOnFormBuilderPage && formBuildingCoachEnabled) &&
		// The coach is a capability of its own, and it reads the review the user is authoring rather
		// than the company's documents — so without this term the trigger opens the panel on the very
		// thing the coach is for and shows a no-documents notice over its suppressed action.
		!(isAuthoringReviewForm && formAuthoringCoachEnabled);

	// Page-contextual actions anchored to the input (PY-14057), matching the FAB's ChatContainer:
	// pre-conversation only, and never over the no-documents notice.
	const showFormBuilderChip =
		isOnFormBuilderPage && formQuickActionEnabled && !conversationStarted && !showNoDocumentsError;
	const showFormBuildingCoachChip =
		isOnFormBuilderPage && formBuildingCoachEnabled && !conversationStarted && !showNoDocumentsError;
	const showGoalCoachChip =
		isOnGoalCreationPage && goalCoachingEnabled && !conversationStarted && !showNoDocumentsError;
	// Contextual, not a card: it only means anything on the review the user is authoring, so it sits
	// with the chat input instead of the empty-state grid of go-anywhere actions.
	const showFormAuthoringCoachAction =
		formAuthoringCoachEnabled && isAuthoringReviewForm && !conversationStarted && !showNoDocumentsError;

	const handleRetry = (): void => {
		const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
		if (!lastUserMessage) return;

		const lastUserMessageIndex = messages.indexOf(lastUserMessage);
		setError(null);

		// History is everything before the message being retried — same as what ChatInput
		// would have sent the first time (the current query is never part of its own history).
		const conversationHistory = messages
			.slice(0, lastUserMessageIndex)
			.filter((m) => m.id !== "greeting")
			.map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));

		// omitUserMessage: the question is already in the thread — re-adding it would show it twice.
		void sendQuery({ query: lastUserMessage.content, messages: conversationHistory, omitUserMessage: true });
	};

	return createPortal(
		<div
			className="fixed top-0 right-0 z-[1300] box-border flex h-full w-full max-w-[420px] animate-in flex-col border-l border-solid border-neutral-2 bg-white shadow-[2px_0px_12px_0px_rgba(47,50,55,0.1)] duration-200 slide-in-from-right-4"
			role="dialog"
			aria-label="Ask PerformYard"
			data-testid="ask-py-panel"
			// Scoped to the panel rather than a window listener: the dock is non-modal, so Escape must
			// not close it while the user is working in the page beside it.
			onKeyDown={(e) => {
				if (e.key === "Escape" && !e.defaultPrevented) {
					closePanel();
				}
			}}
		>
			<AskPyPanelHeader />
			<div className="flex flex-1 flex-col overflow-hidden">
				{showNoDocumentsError ? (
					<ChatErrorBanner type="no-documents" />
				) : isLoadingConversation ? (
					// A restored thread replaces the whole panel body, so showing the
					// empty state's quick-action cards in the meantime would flash a
					// "start a new chat" surface at someone who just opened an old one.
					<div className="flex flex-1 items-center justify-center" data-testid="ask-py-conversation-loading">
						<Spinner />
					</div>
				) : conversationStarted ? (
					<ChatMessages variant="panel" onRetry={handleRetry} />
				) : (
					<AskPyEmptyState onSelect={(options) => void sendQuery(options)} />
				)}
			</div>
			{(showFormBuilderChip || showFormBuildingCoachChip) && (
				<div className="px-5 pb-1">
					<FormBuilderSuggestedActions
						showSuggestQuestions={showFormBuilderChip}
						showSuggestImprovements={showFormBuildingCoachChip}
						onSelectSuggestQuestions={(query) => void sendQuery({ query, agent: "form" })}
						onSelectSuggestImprovements={(query) => void sendQuery({ query, agent: "form-building-coach" })}
					/>
				</div>
			)}
			{showGoalCoachChip && (
				<div className="px-5 pb-1">
					<GoalCoachSuggestedActions onSelect={(query) => void sendQuery({ query, agent: "goal-coach" })} />
				</div>
			)}
			{showFormAuthoringCoachAction && (
				<div className="px-4 pt-2">
					<FormAuthoringCoachSuggestedActions
						onSelect={(query) => void sendQuery({ query, agent: "form-authoring-coach" })}
					/>
				</div>
			)}
			<ChatInput variant="panel" />
		</div>,
		document.body
	);
};

export default AskPyPanel;
